import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MONTHS_ID } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const today = new Date();

// Brand color per platform (recognition di Calendar)
const PLATFORM_COLORS = {
  "Booking.com": { bg: "#0071C2", label: "Booking.com" },          // Booking.com blue
  "Airbnb": { bg: "#FF5A5F", label: "Airbnb" },                    // Airbnb red/pink
  "StayRajaAmpat": { bg: "#D16B4D", label: "StayRajaAmpat" },      // Brand terracotta
  "Direct WhatsApp": { bg: "#25D366", label: "Direct WhatsApp" },  // WhatsApp green
};
const FALLBACK_OCCUPIED = "#586D61";  // sage for unknown / iCal blocked

const getCellColor = (cell) => {
  if (!cell.occupied) return "#2C7A51";  // available - jungle green
  const cfg = PLATFORM_COLORS[cell.platform];
  return cfg ? cfg.bg : FALLBACK_OCCUPIED;
};

export default function CalendarPage() {
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/calendar?year=${year}&month=${month}`);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year, month]);

  const prev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1);
  };

  const days = data?.days_in_month || 0;
  const years = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  const weekdayShort = ["M", "S", "S", "R", "K", "J", "S"];

  return (
    <>
      <PageHeader
        title="Calendar"
        subtitle="Grid ketersediaan kamar — warna sel menunjukkan platform booking."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="btn-prev-month"
              variant="outline" size="icon" onClick={prev}
              className="bg-white border-[#EBE6DE] rounded-xl h-10 w-10"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="calendar-month-select" className="w-[150px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS_ID.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="calendar-year-select" className="w-[110px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              data-testid="btn-next-month"
              variant="outline" size="icon" onClick={next}
              className="bg-white border-[#EBE6DE] rounded-xl h-10 w-10"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#586D61]">
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded bg-[#2C7A51]" /> Tersedia
        </div>
        <span className="h-4 w-px bg-[#EBE6DE] hidden sm:inline-block" />
        {Object.entries(PLATFORM_COLORS).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-2">
            <span className="h-3.5 w-3.5 rounded" style={{ backgroundColor: cfg.bg }} /> {cfg.label}
          </div>
        ))}
        <span className="h-4 w-px bg-[#EBE6DE] hidden sm:inline-block" />
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded" style={{ backgroundColor: FALLBACK_OCCUPIED }} /> Blocked / Lainnya
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded border-2 border-[#D16B4D] bg-white" /> Hari ini
        </div>
      </div>

      <div className="bg-white border border-[#EBE6DE] rounded-2xl shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-[#586D61]">Memuat kalender…</div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div className="min-w-max p-6">
              {/* Header row: date numbers */}
              <div className="flex gap-1 pl-48 mb-2">
                {Array.from({ length: days }, (_, i) => {
                  const d = i + 1;
                  const cur = new Date(year, month - 1, d);
                  const dow = weekdayShort[cur.getDay()];
                  const isToday = cur.toDateString() === today.toDateString();
                  return (
                    <div key={d} className={`w-9 text-center ${isToday ? "text-[#D16B4D] font-bold" : "text-[#586D61]"}`}>
                      <div className="text-[10px] uppercase font-bold tracking-wider">{dow}</div>
                      <div className="text-sm font-mono">{d}</div>
                    </div>
                  );
                })}
              </div>

              {/* Rows */}
              <div className="flex flex-col gap-1.5">
                {data?.rooms?.map((room) => (
                  <div key={room.room_id} className="flex items-center gap-1">
                    <div className="w-48 pr-4 flex-shrink-0">
                      <div className="font-heading text-sm font-medium text-[#172E21] truncate">{room.room_name}</div>
                    </div>
                    {room.days.map((cell) => {
                      const cur = new Date(year, month - 1, cell.day);
                      const isToday = cur.toDateString() === today.toDateString();
                      const bgColor = getCellColor(cell);
                      return (
                        <Tooltip key={cell.date}>
                          <TooltipTrigger asChild>
                            <div
                              data-testid={`calendar-cell-${room.room_name.replace(/\s+/g, "-").toLowerCase()}-${cell.day}`}
                              className={`cal-cell w-9 h-9 rounded-md cursor-default transition-transform hover:scale-110 ${
                                isToday ? "ring-2 ring-[#D16B4D] ring-offset-1" : ""
                              }`}
                              style={{ backgroundColor: bgColor }}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-[#082618] text-white border-0">
                            <div className="text-xs">
                              <div className="font-bold">{room.room_name}</div>
                              <div>{cell.date}</div>
                              <div className="mt-1">
                                {cell.occupied ? (
                                  <>
                                    Tamu: <span className="font-medium">{cell.guest_name || "-"}</span>
                                    <br/>
                                    Platform: <span className="font-medium" style={{ color: bgColor }}>{cell.platform || "Unknown"}</span>
                                  </>
                                ) : (
                                  "Tersedia"
                                )}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </TooltipProvider>
        )}
      </div>
    </>
  );
}
